import { clearTokens } from '@/lib/api';
import { cn } from '@/lib/utils';

import { Link, useRouter } from '@tanstack/react-router';
import { LogOut, Menu, X } from 'lucide-react';
import React from 'react';
import Logo from './logo';

export const Header = ({
  links,
  isLoggedIn = false,
}: {
  links: { label: string; to: string; isScroll: boolean }[];
  isLoggedIn?: boolean;
}) => {
  const [menuState, setMenuState] = React.useState(false);

  const router = useRouter();

  const handleLogout = async () => {
    clearTokens();
    router.navigate({ to: '/login' });
  };

  return (
    <header>
      <nav
        className={cn(
          'fixed z-[100] w-full px-4 md:px-0 text-white bg-black/50  backdrop-blur-lg'
        )}
      >
        <div className={cn('mx-auto container transition-all duration-300')}>
          <div className="relative flex flex-wrap items-center justify-between py-3 gap-2 lg:gap-0 lg:py-4">
            <div className="flex w-full justify-between lg:w-auto">
              <Logo className="!w-24" />

              <button
                data-state={menuState ? 'active' : undefined}
                onClick={() => setMenuState(!menuState)}
                aria-label={menuState ? 'Close Menu' : 'Open Menu'}
                className="relative z-20 -m-2.5 -mr-4 block cursor-pointer p-2.5 lg:hidden"
              >
                <Menu
                  className={cn(
                    'm-auto size-6 duration-200',
                    menuState && 'rotate-180 scale-0 opacity-0'
                  )}
                />
                <X
                  className={cn(
                    'absolute inset-0 m-auto size-6 -rotate-180 scale-0 opacity-0 duration-200',
                    menuState && 'rotate-0 scale-100 opacity-100'
                  )}
                />
              </button>
            </div>

            <div className="absolute inset-0 m-auto hidden size-fit lg:block">
              <ul className="flex gap-8 text-sm font-bold tracking-widest">
                {links.map((item, index) => (
                  <li key={index}>
                    {item.isScroll ? (
                      <button
                        className="cursor-pointer hover:underline"
                        onClick={async () => {
                          const elementId =
                            item.to === '/products' ? 'products' : item.to;
                          const currentPath = router.state.location.pathname;

                          // If we're not on home page and trying to scroll to a home-only section, navigate first
                          if (currentPath !== '/' && item.to.startsWith('/')) {
                            await router.navigate({ to: '/' });
                            setTimeout(() => {
                              document
                                .getElementById(elementId)
                                ?.scrollIntoView({ behavior: 'smooth' });
                            }, 100);
                          } else {
                            document
                              .getElementById(elementId)
                              ?.scrollIntoView({ behavior: 'smooth' });
                          }
                        }}
                      >
                        {item.label}
                      </button>
                    ) : (
                      <Link
                        to={item.to}
                        className="block text-white duration-150 hover:underline"
                      >
                        {item.label}
                      </Link>
                    )}
                  </li>
                ))}
                {isLoggedIn && (
                  <li>
                    <button
                      onClick={handleLogout}
                      className="flex items-center gap-2 cursor-pointer hover:underline"
                      aria-label="Kijelentkezés"
                    >
                      <span>Kijelentkezés</span>
                      <LogOut className="size-4" />
                    </button>
                  </li>
                )}
              </ul>
            </div>

            <div
              data-state={menuState ? 'active' : undefined}
              className="hidden w-full flex-wrap items-center justify-end space-y-8 rounded-sm border bg-background p-6 shadow-2xl shadow-zinc-300/20 md:flex-nowrap lg:m-0 lg:flex lg:w-fit lg:gap-6 lg:space-y-0 lg:border-transparent lg:bg-transparent lg:p-0 lg:shadow-none dark:shadow-none dark:lg:bg-transparent data-[state=active]:block mb-6 lg:data-[state=active]:flex"
            >
              <div className="lg:hidden">
                <ul className="space-y-6 text-base">
                  {links.map((item, index) => (
                    <li key={index}>
                      <Link
                        to={item.to}
                        className="block text-muted-foreground duration-150 hover:text-accent-foreground"
                      >
                        <span>{item.label}</span>
                      </Link>
                    </li>
                  ))}
                  {isLoggedIn && (
                    <li>
                      <button
                        onClick={handleLogout}
                        className="flex items-center gap-2 text-muted-foreground duration-150 hover:text-accent-foreground"
                        aria-label="Kijelentkezés"
                      >
                        <span>Kijelentkezés</span>
                        <LogOut className="size-4" />
                      </button>
                    </li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </nav>
    </header>
  );
};
